import React, { useEffect, useMemo, useState } from "react";
import { CmdIcon } from "../shared/CmdIcon";
import { ProjectLocationsSection } from "./ProjectLocationsSection";
import { ProjectOfflineBadge } from "./ProjectOfflineBadge";
import { photoCountLabel } from "../../helpers/projectPictures";
import { classifyTask, summarizeProjectTasks } from "../../helpers/dashboard/commandDashboardModel";
import { dayIndex, localTodayISO } from "../../helpers/planning/planningModel";
import { formatTaskDate } from "../../helpers/tasks/taskModel";
import { loadUsersByAuthIdsForOrg } from "../../helpers/scopedQueries";
import { loadLatestPictures, loadRecentConversations } from "../../helpers/project/projectOverviewData";
import { supabase } from "../../supabase";
import { isOffline } from "../../helpers/connectivity";
import "./projectDesktopOverview.css";

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
}

function targetCountdown(iso, todayIso) {
  const target = dayIndex(iso);
  const today = dayIndex(todayIso);
  if (target == null || today == null) return "";
  const diff = target - today;
  if (diff === 0) return "Due today";
  if (diff > 0) return diff < 14 ? `In ${diff} day${diff === 1 ? "" : "s"}` : `In ${Math.round(diff / 7)} weeks`;
  return `${-diff} day${diff === -1 ? "" : "s"} past target`;
}

function relativeWhen(value, todayIso) {
  const day = String(value || "").slice(0, 10);
  const a = dayIndex(day);
  const b = dayIndex(todayIso);
  if (a == null || b == null) return "";
  const diff = b - a;
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return formatTaskDate(day, { year: false });
}

const TASK_PILL = {
  overdue: ["Overdue", "red"],
  today: ["Today", "gold"],
  upcoming: ["Upcoming", "blue"],
  unscheduled: ["No date", "grey"],
};

/**
 * Desktop Project Overview (concept CP-DSK-PRJ-OVR-v01). Pure composition over
 * data ProjectDetailScreen already holds (tasks, costs, logs, locations) plus
 * three read-only lists (conversations, pictures, team names). Every
 * action here is an existing ProjectWorkspaceHome action.
 */
export function ProjectDesktopOverview({
  actor,
  title,
  clientName,
  projectNumber,
  statusLabel,
  offlineState = "",
  onRetryOffline,
  showEdit,
  onEdit,
  totalHoursLabel,
  showFinancial,
  showOperationalMaterials = false,
  labourLabel,
  materialsLabel,
  otherCostsLabel,
  totalLabel,
  targetDate,
  address,
  description,
  projectId,
  editable,
  onOpenLocation,
  tasks = [],
  structure = [],
  tasksEnabled,
  logs = [],
  sessionCount = 0,
  pictureCount = 0,
  showTasks,
  onOpenTasks,
  showConversations,
  onOpenConversations,
  showClientView,
  clientViewLabel,
  onOpenClientView,
  showInvoicing,
  onOpenPictures,
  onOpenActivity,
  onOpenLabour,
  onOpenMaterials,
  onOpenOtherCosts,
  onOpenInvoicing,
}) {
  const today = useMemo(() => localTodayISO(new Date()), []);
  const todayIdx = dayIndex(today);
  const [conversations, setConversations] = useState([]);
  const [pictures, setPictures] = useState([]);
  const [names, setNames] = useState({});
  const showOffline = offlineState && offlineState !== "attention";

  useEffect(() => {
    let cancelled = false;
    if (!projectId) return undefined;
    loadRecentConversations(actor, projectId).then((rows) => !cancelled && setConversations(rows)).catch(() => {});
    loadLatestPictures(actor, projectId).then((rows) => !cancelled && setPictures(rows)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actor, projectId, pictureCount]);

  const locationName = useMemo(() => new Map((structure || []).map((l) => [l.project_location_id, l.name])), [structure]);

  // Same task-health definition as the Dashboard Project Health and the Project cards.
  const progress = useMemo(() => summarizeProjectTasks(tasks, today), [tasks, today]);

  const nextUp = useMemo(
    () =>
      (tasks || [])
        .filter((task) => task.is_active !== false && task.status !== "completed")
        .sort((a, b) => {
          const da = dayIndex(a.due_date);
          const db = dayIndex(b.due_date);
          if (da == null && db == null) return String(a.title).localeCompare(String(b.title));
          if (da == null) return 1;
          if (db == null) return -1;
          return da - db;
        })
        .slice(0, 5),
    [tasks],
  );

  const team = useMemo(() => {
    const counts = new Map();
    (tasks || []).forEach((task) => {
      if (task.is_active === false || task.status === "completed" || !task.assignee_id) return;
      counts.set(task.assignee_id, (counts.get(task.assignee_id) || 0) + 1);
    });
    return [...counts.entries()].map(([id, open]) => ({ id, open })).sort((a, b) => b.open - a.open);
  }, [tasks]);

  const teamKey = team.map((m) => m.id).join(",");
  useEffect(() => {
    if (!team.length || isOffline()) return undefined;
    let cancelled = false;
    loadUsersByAuthIdsForOrg(supabase, actor, team.map((m) => m.id), "supabase_user_id, employee_name")
      .then((users) => {
        if (cancelled) return;
        setNames(Object.fromEntries((users || []).map((u) => [u.supabase_user_id, u.employee_name])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, teamKey]);

  const recentLogs = useMemo(
    () => [...(logs || [])].sort((a, b) => String(b.date_iso || "").localeCompare(String(a.date_iso || ""))).slice(0, 4),
    [logs],
  );

  const mapsHref = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : "";

  const tabs = [
    { id: "overview", label: "Overview", on: true },
    showTasks ? { id: "tasks", label: "Tasks", onClick: onOpenTasks } : null,
    showConversations ? { id: "conv", label: "Conversations", onClick: onOpenConversations } : null,
    { id: "pictures", label: "Pictures", onClick: onOpenPictures, count: pictureCount },
    { id: "activity", label: "Activity", onClick: onOpenActivity },
    showOperationalMaterials
      ? { id: "materials", label: "Materials", onClick: onOpenMaterials }
      : null,
    showInvoicing ? { id: "invoicing", label: "Invoicing", onClick: onOpenInvoicing } : null,
    showClientView ? { id: "client", label: "Client View", onClick: onOpenClientView } : null,
  ].filter(Boolean);

  return (
    <div className="pdo" data-testid="project-desktop-overview">
      <header className="pdo-head">
        <div className="pdo-head__photo" aria-hidden="true"><CmdIcon name="folder" /></div>
        <div className="pdo-head__main">
          <h1 className="pdo-head__title">{title}</h1>
          <p className="pdo-head__line">
            {[clientName || "No client", address].filter(Boolean).join(" · ")}
          </p>
          <div className="pdo-head__chips">
            {statusLabel ? <span className="pj-status">{statusLabel}</span> : null}
            {showOffline ? <ProjectOfflineBadge state={offlineState} /> : null}
            {projectNumber ? <span className="pdo-head__num">{projectNumber}</span> : null}
          </div>
          {offlineState === "attention" ? <ProjectOfflineBadge state={offlineState} block onRetry={onRetryOffline} /> : null}
        </div>
        <div className="pdo-head__metrics">
          <div className="pdo-stat">
            <CmdIcon name="calendar" />
            <span className="pdo-stat__label">Target Completion</span>
            <b>{targetDate ? formatTaskDate(String(targetDate).slice(0, 10), { year: true }) : "Not set"}</b>
            <span className="pdo-stat__sub">{targetDate ? targetCountdown(String(targetDate).slice(0, 10), today) : "Set an estimated end date to track"}</span>
          </div>
          <div className="pdo-stat">
            <CmdIcon name="clock" />
            <span className="pdo-stat__label">Total Hours</span>
            <b>{totalHoursLabel}</b>
            <span className="pdo-stat__sub">{sessionCount} session{sessionCount === 1 ? "" : "s"}</span>
          </div>
          {showFinancial ? (
            <div className="pdo-stat pdo-stat--cost">
              <CmdIcon name="dollar" />
              <span className="pdo-stat__label">Project Cost</span>
              <b>{totalLabel}</b>
              <span className="pdo-stat__break">
                <button type="button" onClick={onOpenLabour}>Labour <i>{labourLabel}</i></button>
                <button type="button" onClick={onOpenMaterials}>Materials <i>{materialsLabel}</i></button>
                <button type="button" onClick={onOpenOtherCosts}>Other <i>{otherCostsLabel}</i></button>
              </span>
            </div>
          ) : null}
        </div>
        {showEdit ? (
          <button type="button" className="pdo-edit" onClick={onEdit}>
            <CmdIcon name="edit" /> Edit Project
          </button>
        ) : null}
      </header>

      <nav className="pdo-tabs" aria-label="Project sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pdo-tab${tab.on ? " is-on" : ""}`}
            aria-current={tab.on ? "page" : undefined}
            onClick={tab.onClick}
            disabled={!tab.on && !tab.onClick}
          >
            {tab.label}
            {tab.count ? <span className="pdo-tab__count">{tab.count}</span> : null}
          </button>
        ))}
      </nav>

      <div className="pdo-cols">
        <div className="pdo-main">
          <section className="pdo-card" data-testid="pdo-progress">
            <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="check" /></span>Project Progress</h2>
            {tasksEnabled && progress.total > 0 ? (
              <>
                <div className="pdo-progress">
                  <span className="pdo-progress__bar"><span style={{ width: `${progress.percent}%` }} /></span>
                  <b>{progress.percent}%</b>
                </div>
                <div className="pdo-progress__meta">
                  <span>{progress.completed} of {progress.total} tasks completed</span>
                  <span className="pj-tchip pj-tchip--red">{progress.overdue} overdue</span>
                  <span className="pj-tchip pj-tchip--gold">{progress.today} today</span>
                  <span className="pj-tchip pj-tchip--blue">{progress.upcoming} upcoming</span>
                  <span className="pj-tchip pj-tchip--grey">{progress.remaining} remaining</span>
                </div>
              </>
            ) : (
              <p className="pdo-empty">{tasksEnabled ? "No tasks yet — progress appears once tasks are added." : "Task progress isn’t available for this account."}</p>
            )}
          </section>

          <section className="pdo-card pdo-card--locations" data-testid="pdo-locations">
            {projectId ? (
              <ProjectLocationsSection projectId={projectId} editable={editable} onOpenLocation={onOpenLocation} />
            ) : null}
          </section>

          {showTasks ? (
            <section className="pdo-card" data-testid="pdo-next-up">
              <h2 className="pdo-card__title">
                <span className="pdo-card__icon"><CmdIcon name="calendar" /></span>Next Up
                <button type="button" className="pdo-link" onClick={onOpenTasks}>View All Tasks <CmdIcon name="arrow" className="pdo-link__arrow" /></button>
              </h2>
              {nextUp.length ? (
                <ul className="pdo-tasks">
                  {nextUp.map((task) => {
                    const kind = classifyTask(task, todayIdx ?? 0);
                    const [pillText, pillTone] = TASK_PILL[kind] || TASK_PILL.unscheduled;
                    return (
                      <li key={task.project_task_id}>
                        <span className="pdo-tasks__date">{task.due_date ? formatTaskDate(task.due_date).toUpperCase() : "—"}</span>
                        <span className="pdo-tasks__title">{task.title}</span>
                        <span className="pdo-tasks__loc">{locationName.get(task.location_id) || "General"}</span>
                        <span className={`pj-tchip pj-tchip--${pillTone}`}>{pillText}</span>
                        <span className="pdo-tasks__who">{task.assignee_id ? names[task.assignee_id] || "Assigned" : "Unassigned"}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="pdo-empty">{tasksEnabled ? "No open tasks." : "Tasks aren’t available for this account."}</p>
              )}
            </section>
          ) : null}

          <div className="pdo-trio">
            <section className="pdo-card" data-testid="pdo-activity">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="pulse" /></span>Recent Activity
                <button type="button" className="pdo-link" onClick={onOpenActivity}>View All</button></h2>
              {recentLogs.length ? (
                <ul className="pdo-feed">
                  {recentLogs.map((log, i) => (
                    <li key={log.id || i}>
                      <span className="pdo-feed__icon"><CmdIcon name="clock" /></span>
                      <span className="pdo-feed__text"><b>{log.work_completed || log.work_category || "Work session"}</b><span>{[log.employee, `${Number(log.total_hours || 0).toFixed(1)} hrs`].filter(Boolean).join(" · ")}</span></span>
                      <span className="pdo-feed__time">{relativeWhen(log.date_iso, today)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pdo-empty">No time logged on this project yet.</p>
              )}
            </section>

            {showConversations ? (
              <section className="pdo-card" data-testid="pdo-conversations">
                <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="chat" /></span>Recent Conversations
                  <button type="button" className="pdo-link" onClick={onOpenConversations}>View All</button></h2>
                {conversations.length ? (
                  <ul className="pdo-feed">
                    {conversations.map((c) => (
                      <li key={c.id}>
                        <span className="pdo-feed__icon"><CmdIcon name="chat" /></span>
                        <span className="pdo-feed__text"><b>{c.title}</b><span>{c.snippet ? `“${c.snippet}…”` : "Transcript saved"}</span></span>
                        <span className="pdo-feed__time">{relativeWhen(c.at, today)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="pdo-empty">No conversations recorded yet.</p>
                )}
              </section>
            ) : null}

            <section className="pdo-card" data-testid="pdo-photos">
              <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="image" /></span>Latest Photos
                <button type="button" className="pdo-link" onClick={onOpenPictures}>View All</button></h2>
              <div className="pdo-photos">
                {pictures.map((pic) => (
                  <button key={pic.picture_id} type="button" className="pdo-photos__tile" onClick={onOpenPictures} aria-label="Open pictures">
                    <img src={pic.previewUrl} alt="" loading="lazy" />
                  </button>
                ))}
                <button type="button" className="pdo-photos__add" onClick={onOpenPictures}>
                  <CmdIcon name="plus" />
                  <span>{pictures.length ? "Add Photos" : photoCountLabel(pictureCount)}</span>
                </button>
              </div>
            </section>
          </div>
        </div>

        <aside className="pdo-rail">
          <section className="pdo-card" data-testid="pdo-summary">
            <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="doc" /></span>Project Summary
              {showEdit ? <button type="button" className="pdo-link" onClick={onEdit}>Edit</button> : null}</h2>
            <dl className="pdo-kv">
              <div><dt>Client</dt><dd>{clientName || "—"}</dd></div>
              <div><dt>Address</dt><dd>{address || "—"}</dd></div>
              <div><dt>Status</dt><dd>{statusLabel ? <span className="pj-status">{statusLabel}</span> : "—"}</dd></div>
              <div><dt>Target Completion</dt><dd>{targetDate ? formatTaskDate(String(targetDate).slice(0, 10), { year: true }) : "Not set"}</dd></div>
              {showFinancial ? <div><dt>Total Project Cost</dt><dd>{totalLabel}</dd></div> : null}
              {showClientView ? <div><dt>Client View</dt><dd>{clientViewLabel}</dd></div> : null}
            </dl>
            {description ? <p className="pdo-desc">{description}</p> : null}
          </section>

          <section className="pdo-card" data-testid="pdo-team">
            <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="users" /></span>Team</h2>
            {team.length ? (
              <ul className="pdo-team">
                {team.map((member) => (
                  <li key={member.id}>
                    <span className="pdo-team__avatar">{initials(names[member.id])}</span>
                    <span className="pdo-team__name">{names[member.id] || "Team member"}</span>
                    <span className="pdo-team__count">{member.open} open</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pdo-empty">No one is assigned to open tasks.</p>
            )}
          </section>

          <section className="pdo-card" data-testid="pdo-map">
            <h2 className="pdo-card__title"><span className="pdo-card__icon"><CmdIcon name="pin" /></span>Map &amp; Address
              {mapsHref ? <a className="pdo-link" href={mapsHref} target="_blank" rel="noopener noreferrer">View in Maps <CmdIcon name="external" className="pdo-link__arrow" /></a> : null}</h2>
            <div className="pdo-map"><CmdIcon name="pin" /><span>{address || "No site address on file."}</span></div>
          </section>
        </aside>
      </div>
    </div>
  );
}
